import { useQuery } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import { useAuth } from '@/hooks/useAuth';
import type { BrandCollision, HomeSupplier, SaleLine } from './storeHomeModel';

export interface StoreHomeKpis {
    pendingShipment: number;
    paid: number;
    processing: number;
    shipped: number;
    delivered: number;
    revenueMonthCents: number;
    revenueAllTimeCents: number;
    totalOrders: number;
    avgTicketCents: number;
    goodsCostCents: number | null;
}

export interface StoreHomeSummary {
    kpis: StoreHomeKpis;
    unshippedCount: number;
    oldestUnshippedPaidAt: string | null;
    abandonedCount: number;
    saleLines: SaleLine[];
    shippingMonthCents: number;
    shippingAllCents: number;
    suppliers: HomeSupplier[];
    brandCollisions: BrandCollision[];
    incompletePublished: number;
    withdrawalsPending: number;
    openReports: number;
    pendingBrandCount: number;
    missingManufacturerCount: number;
}

function num(value: unknown): number {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
}

function asSummary(raw: Partial<StoreHomeSummary> | undefined): StoreHomeSummary {
    const kpis = (raw?.kpis ?? {}) as Partial<StoreHomeKpis>;
    return {
        kpis: {
            pendingShipment: num(kpis.pendingShipment),
            paid: num(kpis.paid),
            processing: num(kpis.processing),
            shipped: num(kpis.shipped),
            delivered: num(kpis.delivered),
            revenueMonthCents: num(kpis.revenueMonthCents),
            revenueAllTimeCents: num(kpis.revenueAllTimeCents),
            totalOrders: num(kpis.totalOrders),
            avgTicketCents: num(kpis.avgTicketCents),
            goodsCostCents: kpis.goodsCostCents == null ? null : num(kpis.goodsCostCents),
        },
        unshippedCount: num(raw?.unshippedCount),
        oldestUnshippedPaidAt: raw?.oldestUnshippedPaidAt ?? null,
        abandonedCount: num(raw?.abandonedCount),
        saleLines: Array.isArray(raw?.saleLines)
            ? raw.saleLines.map((line) => ({ ...line, cents: num(line.cents) }))
            : [],
        shippingMonthCents: num(raw?.shippingMonthCents),
        shippingAllCents: num(raw?.shippingAllCents),
        suppliers: Array.isArray(raw?.suppliers) ? raw.suppliers : [],
            brandCollisions: Array.isArray(raw?.brandCollisions)
            ? raw.brandCollisions.map((row) => ({
                ...row,
                showBrand: row.showBrand === true,
                productCount: num(row.productCount),
            }))
            : [],
        incompletePublished: num(raw?.incompletePublished),
        withdrawalsPending: num(raw?.withdrawalsPending),
        openReports: num(raw?.openReports),
        pendingBrandCount: num(raw?.pendingBrandCount),
        missingManufacturerCount: num(raw?.missingManufacturerCount),
    };
}

export function useStoreHome() {
    const { user } = useAuth();
    return useQuery({
        queryKey: ['admin', 'store-home'],
        enabled: Boolean(user?.id),
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_store_home_summary', {
                p_admin_user_id: user!.id,
            });
            if (error) throw new Error(error.message);
            const summary = asSummary(normalizeRpcResponse(data)[0] as Partial<StoreHomeSummary> | undefined);
            const tasks = await dbClient.rpc('admin_store_brand_tasks', {
                p_admin_user_id: user!.id,
            });
            if (tasks.error) throw new Error(tasks.error.message);
            const taskRow = (normalizeRpcResponse(tasks.data)[0] ?? {}) as {
                pendingCount?: number;
                missingManufacturerCount?: number;
            };
            return {
                ...summary,
                pendingBrandCount: num(taskRow.pendingCount),
                missingManufacturerCount: num(taskRow.missingManufacturerCount),
            };
        },
        staleTime: 30_000,
    });
}
